import { useEffect, useState } from 'react';
import {
  FaBook,
  FaChurch,
  FaCross,
  FaDove,
  FaEye,
  FaHandHoldingHeart,
  FaShieldAlt,
} from 'react-icons/fa';
import { Link } from 'react-router-dom';

import bibleStudyImage from '../../assets/BibleStudy01.jpg';

const About = () => {
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const baseUrl = import.meta.env.BASE_URL;
  const leaderImages = {
    leaderOne: `${baseUrl}assets/people/leader-01.jpg`,
    leaderTwo: `${baseUrl}assets/people/leader-02.jpg`,
    leaderThree: `${baseUrl}assets/people/leader-03.jpg`,
    leaderFour: `${baseUrl}assets/people/leader-04.jpg`,
  };
  const earlyBeginningsPhoto = `${baseUrl}assets/history/19850905-MFC-03.jpg`;
  const earlyBeginningsPhotoThumb = `${baseUrl}assets/history/19850905-MFC-03-thumb.jpg`;

  const beliefs = [
    {
      title: 'The Bible',
      content:
        'We believe the Bible is the inspired, infallible, and authoritative Word of God, providing all that is needed for life and godliness.',
      icon: FaBook,
    },
    {
      title: 'The Trinity',
      content:
        'We believe in one God, eternally existing in three persons: Father, Son, and Holy Spirit, each with distinct personal attributes.',
      icon: FaShieldAlt,
    },
    {
      title: 'Jesus Christ',
      content:
        'We believe in the deity of Jesus Christ, His virgin birth, sinless life, miracles, atoning death, bodily resurrection, and personal return.',
      icon: FaCross,
    },
    {
      title: 'Salvation',
      content:
        'We believe that salvation is by grace alone through faith alone in Christ alone, apart from any human merit or works.',
      icon: FaHandHoldingHeart,
    },
    {
      title: 'The Holy Spirit',
      content:
        'We believe in the present ministry of the Holy Spirit, who indwells all believers and empowers them for godly living and service.',
      icon: FaDove,
    },
    {
      title: 'The Church',
      content:
        'We believe the universal Church is made up of all who have placed their faith in Jesus Christ as Savior and Lord. We also affirm the importance of the local church-gathering regularly for worship, discipleship, fellowship, and mission.',
      icon: FaChurch,
    },
  ];

  useEffect(() => {
    if (!isArchiveModalOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsArchiveModalOpen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isArchiveModalOpen]);

  return (
    <div className="py-12">
      <section className="hero-brand hero-brand-rocky text-white py-20">
        <div className="container text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">About Our Ministry</h1>
          <p className="text-xl max-w-3xl mx-auto">
            A community of believers committed to spreading the Gospel and making disciples of all
            nations.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="container">
          <div>
            <h2 className="text-3xl font-bold mb-6">Our Story</h2>

            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-lg text-brand-muted">
                  <strong>Early Beginnings:</strong> Ministries For Christ began in 1977 when Stu
                  Slimp, together with his wife Carole, stepped out in faith to share biblical truth
                  and practical encouragement through a simple tract ministry in their garage in
                  Austin, Texas. Their ministry grew from that humble beginning into a thriving
                  outreach and, by 1986, expanded into the largest Christian retail store in Texas,
                  while still keeping Scripture, prayer, and service at the center. A devastating
                  arson fire in 1986 set things back, but with God&apos;s provision was able to run
                  for another 10 years, finally shutting down in the late 1990s,
                </p>
              </div>
              <div className="w-full flex justify-center md:justify-start">
                <figure className="w-full max-w-xs text-center">
                  <button
                    type="button"
                    className="block w-full"
                    onClick={() => setIsArchiveModalOpen(true)}
                    aria-label="Open full-size Early Beginnings archive photo"
                  >
                    <img
                      src={earlyBeginningsPhotoThumb}
                      alt="Historical Ministries For Christ newspaper-era clipping"
                      width={560}
                      height={351}
                      className="w-full h-auto rounded-md shadow-md cursor-zoom-in"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                  <figcaption className="mt-2 text-sm text-brand-muted">
                    Early Beginnings archive photo
                  </figcaption>
                </figure>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-start mt-8">
              <div>
                <p className="text-lg text-brand-muted mb-8">
                  <strong>Currently:</strong> Again rekindled, the ministry continues today by
                  God&apos;s grace with a focus on helping people draw closer to God through
                  faithful Bible teaching, encouragement, and discipleship. We are building
                  Christ-centered media, creative tracts, worship music, video teaching content, and
                  practical resources that strengthen everyday Christian living, support prayer and
                  Scripture habits, and provide a welcoming place for people to learn, ask
                  questions, and grow in faith.
                </p>
                <Link to="/contact" className="btn btn-primary">
                  Join Us on this Journey
                </Link>
              </div>
              <div className="w-full flex justify-center md:justify-start">
                <div className="relative w-full max-w-2xl bg-gray-100 rounded-lg overflow-hidden shadow-xl">
                  <img
                    src={bibleStudyImage}
                    alt="Diverse group of people engaged in Bible study in a cozy living room"
                    width={1280}
                    height={1920}
                    className="w-full h-auto rounded-lg"
                    style={{ maxHeight: '70vh' }}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-soft">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Mission & Vision</h2>
            <p className="text-lg text-brand-muted max-w-3xl mx-auto">
              Guided by Scripture, we strive to fulfill the Great Commission in our generation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="card">
              <div className="p-6">
                <div className="icon-brand text-4xl mb-4">
                  <FaCross />
                </div>
                <h3 className="text-2xl font-bold mb-4">Our Mission</h3>
                <p className="text-brand-muted">
                  To make disciples of Jesus Christ who love God, love others, and serve the world
                  with the transforming power of the Gospel.
                </p>
              </div>
            </div>

            <div className="card">
              <div className="p-6">
                <div className="icon-brand text-4xl mb-4">
                  <FaEye />
                </div>
                <h3 className="text-2xl font-bold mb-4">Our Vision</h3>
                <p className="text-brand-muted">
                  To be a thriving, community of believers who are being transformed by the Gospel
                  and actively engaged in God&apos;s redemptive work in the world.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Core Beliefs</h2>
            <p className="text-lg text-brand-muted max-w-3xl mx-auto">
              Rooted in Scripture, centered on the Gospel, and committed to the historic Christian
              faith.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {beliefs.map((belief, index) => {
              const Icon = belief.icon;
              return (
                <div
                  key={index}
                  className="card bg-belief-card hover:shadow-lg transition-shadow duration-300"
                >
                  <div className="p-6">
                    <div className="icon-brand text-3xl mb-4">
                      <Icon />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{belief.title}</h3>
                    <p className="text-brand-muted">{belief.content}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-soft">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Leadership</h2>
            <p className="text-lg text-brand-muted max-w-3xl mx-auto">
              Meet the team that guides and shepherds our church family.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                name: 'Jon Beck',
                role: 'Spiritual Leader',
                bio: 'With over 20 years of experience, Jon helps guide the overall vision and direction with a humble, prayerful heart and a steady focus on honoring God in every step.',
                img: leaderImages.leaderOne,
              },
              {
                name: 'Sarah Johnson',
                role: 'Prayer Director',
                bio: 'Sarah leads our prayer ministry by organizing prayer initiatives, encouraging consistent intercession, and helping our church family remain rooted in prayer, care, and spiritual support.',
                img: leaderImages.leaderTwo,
              },
              {
                name: 'Michael Chen',
                role: 'Administrative Lead',
                bio: 'Michael oversees ministry operations, scheduling, and coordination, helping teams stay organized and supported so outreach, teaching, and care efforts run smoothly.',
                img: leaderImages.leaderThree,
              },
              {
                name: 'David Slimp',
                role: 'Lead Tech/AI Designer',
                bio: 'David oversees our digital presence and technology initiatives, ensuring we effectively use modern tools to spread the Gospel.',
                img: leaderImages.leaderFour,
              },
            ].map((leader, index) => (
              <div key={index} className="text-center">
                <div className="w-48 h-48 mx-auto mb-6 rounded-full overflow-hidden border-4 border-white shadow-lg relative bg-gray-100">
                  <img
                    src={leader.img}
                    alt={leader.name}
                    width={192}
                    height={192}
                    className="w-full h-full object-cover absolute inset-0"
                    style={{ objectPosition: 'center', aspectRatio: '1/1' }}
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <h3 className="text-xl font-bold">{leader.name}</h3>
                <p className="text-brand-primary font-medium mb-3">{leader.role}</p>
                <p className="text-brand-muted">{leader.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-primary text-white">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-6 text-white">Want to Learn More?</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto">
            We&apos;d love to connect with you and help you take your next step in following Jesus.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="btn btn-primary">
              Contact Us
            </Link>
            <Link to="/" className="btn btn-primary">
              Plan a Visit
            </Link>
          </div>
        </div>
      </section>

      {isArchiveModalOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/80 p-4 md:p-8 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-label="Early Beginnings archive newspaper image"
        >
          <div className="relative w-full max-w-5xl max-h-[90vh] flex flex-col items-center">
            <button
              type="button"
              className="btn btn-primary self-end mb-3"
              onClick={() => setIsArchiveModalOpen(false)}
            >
              Close
            </button>
            <img
              src={earlyBeginningsPhoto}
              alt="Full-size Early Beginnings archive newspaper clipping"
              width={3420}
              height={2148}
              className="w-auto h-auto max-w-full max-h-[82vh] rounded-md shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default About;
